import { getCurrentDate } from "core/utils/constants/DateTime";
import React from "react";

// Global cache for static images to persist across re-renders
const staticImageCache = new Map<string, string>();

// Track which images are currently being loaded to prevent duplicate loads
const loadingImages = new Set<string>();

// Track animated banners that have finished loading to avoid showing the hover loader again.
const animatedImageCache = new Set<string>();

const EventSetupCard = React.memo(({
  isSelected,
  onToggle,
  card,
  showAnimatedImageLoader = false,
}: {
  isSelected: boolean;
  onToggle: () => void;
  card: any;
  showAnimatedImageLoader?: boolean;
}) => {
  const [isHovered, setIsHovered] = React.useState(false);
  const bannerUrl = card?.BannerUrl || card?.BANNER_URL;
  const bannerImageUrl = card?.BANNER_IMG_URL || card?.Banner_img_url || card?.banner_img_url;
  const [isAnimatedImageLoading, setIsAnimatedImageLoading] = React.useState(false);

  // Check cache immediately to prevent blinking - if cached, don't show loading
  const hasCachedImage = bannerUrl && staticImageCache.has(bannerUrl);
  const [isLoading, setIsLoading] = React.useState(!bannerImageUrl && !hasCachedImage);
  const [staticImage, setStaticImage] = React.useState<string | null>(() => {
    if (hasCachedImage) {
      return staticImageCache.get(bannerUrl) || null;
    }
    return null;
  });

  // Extract first frame from GIF on mount to use as static preview
  React.useEffect(() => {
    if (bannerImageUrl || !bannerUrl) {
      setIsLoading(false);
      return;
    }

    // Check cache first
    if (staticImageCache.has(bannerUrl)) {
      setStaticImage(staticImageCache.get(bannerUrl)!);
      setIsLoading(false);
      return;
    }

    // Prevent duplicate loading
    if (loadingImages.has(bannerUrl)) {
      return;
    }

    loadingImages.add(bannerUrl);
    setIsLoading(true);

    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(img, 0, 0);
          const dataUrl = canvas.toDataURL('image/png');
          // Store in cache
          staticImageCache.set(bannerUrl, dataUrl);
          setStaticImage(dataUrl);
        }
      } catch (e) {
        // If CORS error or canvas tainted, fall back to original URL
        setStaticImage(null);
      }
      loadingImages.delete(bannerUrl);
      setIsLoading(false);
    };
    img.onerror = () => {
      loadingImages.delete(bannerUrl);
      setStaticImage(null);
      setIsLoading(false);
    };
    img.src = bannerUrl;
  }, [bannerImageUrl, bannerUrl]);

  React.useEffect(() => {
    setIsAnimatedImageLoading(false);
  }, [bannerUrl]);

  const staticImageSrc = bannerImageUrl || staticImage || bannerUrl;
  const shouldShowAnimatedBanner = isHovered && Boolean(bannerUrl);

  const handleMouseEnter = () => {
    setIsHovered(true);
    setIsAnimatedImageLoading(
      showAnimatedImageLoader
      && Boolean(bannerUrl)
      && !animatedImageCache.has(bannerUrl),
    );
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    setIsAnimatedImageLoading(false);
  };

  return <div className={`cursor-pointer flex flex-col items-center bg-neutral-light rounded-xl shadow-md border border-solid p-3 gap-2 h-full transition-transform duration-300 ${isSelected ? "bg-[#D63855] text-neutral-light" : ""
    }`}
    style={{
      transform: isSelected ? 'rotate(10deg)' : 'rotate(0deg)',
    }}
    onMouseEnter={handleMouseEnter}
    onMouseLeave={handleMouseLeave}
    onClick={onToggle}>
    <div className="relative rounded-xl overflow-clip min-h-[80%] max-h-[80%] border border-solid bg-neutral-30 w-full flex items-center justify-center">
      {isLoading ? (
        // Skeleton loader
        <div className="w-full h-full animate-pulse bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 bg-[length:200%_100%]"
          style={{
            animation: 'shimmer 1.5s infinite',
            minHeight: '150px'
          }}
        >
          <style>{`
            @keyframes shimmer {
              0% { background-position: 200% 0; }
              100% { background-position: -200% 0; }
            }
          `}</style>
        </div>
      ) : (
        <>
          <img
            src={staticImageSrc}
            alt={card?.EVENT_NAME || card?.TITLE || 'Event'}
            className='object-cover w-full h-full'
            loading="lazy"
            decoding="async"
          />
          {shouldShowAnimatedBanner && (
            <img
              src={bannerUrl}
              alt=""
              aria-hidden="true"
              className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-150 ${isAnimatedImageLoading ? 'opacity-0' : 'opacity-100'}`}
              decoding="async"
              onLoad={() => {
                animatedImageCache.add(bannerUrl);
                setIsAnimatedImageLoading(false);
              }}
              onError={() => setIsAnimatedImageLoading(false)}
            />
          )}
          {showAnimatedImageLoader && isAnimatedImageLoading && (
            <div
              className="absolute inset-0 flex items-center justify-center bg-black/15"
              role="status"
              aria-label="Loading animated preview"
            >
              <span className="h-[4vh] w-[4vh] animate-spin rounded-full border-[0.35vh] border-white/50 border-t-white shadow-sm" />
            </div>
          )}
        </>
      )}
    </div>
    <div className='text-center max-w-[90%]'>
      <p className="text-sm font-bold text-ellipsis truncate">{card?.TITLE}</p>
      {(!!card?.START_DATE || !!card?.EVENT_DATE) && <small className='text-xs'>{!!card?.START_DATE ? "" : "Event Date:"} {getCurrentDate(card?.START_DATE || card?.EVENT_DATE)}</small>}
    </div>
  </div>;
}, (prevProps, nextProps) => {
  // Custom comparison - only re-render if these specific props change
  return prevProps.isSelected === nextProps.isSelected &&
    prevProps.card?.MASTER_EVENT_ID === nextProps.card?.MASTER_EVENT_ID &&
    prevProps.card?.BannerUrl === nextProps.card?.BannerUrl &&
    prevProps.card?.BANNER_URL === nextProps.card?.BANNER_URL &&
    prevProps.card?.BANNER_IMG_URL === nextProps.card?.BANNER_IMG_URL &&
    prevProps.card?.Banner_img_url === nextProps.card?.Banner_img_url &&
    prevProps.showAnimatedImageLoader === nextProps.showAnimatedImageLoader;
});

export default EventSetupCard;
